const GA_TRACKING_ID = 'G-H320E9D18X';

class AnalyticsManager {
  constructor() {
    this.isInitialized = false;
    this.isEnabled = true;
    this.debugMode = process.env.NODE_ENV === 'development';
  }

  initialize() {
    if (this.isInitialized || typeof window === 'undefined') {
      return;
    }

    try {
      // Load Google Analytics script
      const script = document.createElement('script');
      script.async = true;
      script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_TRACKING_ID}`;
      document.head.appendChild(script);

      // Initialize gtag
      window.dataLayer = window.dataLayer || [];
      window.gtag = function() {
        window.dataLayer.push(arguments);
      };

      window.gtag('js', new Date());
      window.gtag('config', GA_TRACKING_ID, {
        page_title: 'BeOnApex - NASCAR Analytics',
        page_location: window.location.href,
        anonymize_ip: true,
        allow_google_signals: false,
        allow_ad_personalization_signals: false,
        cookie_expires: 63072000, // 2 years
        send_page_view: true
      });

      this.isInitialized = true;
      
      if (this.debugMode) {
        console.log('📊 Google Analytics 4 initialized successfully');
      }
    } catch (error) {
      console.error('❌ Failed to initialize Google Analytics:', error);
      this.isEnabled = false;
    }
  }

  // Track page views
  trackPageView(pageName, pageTitle = null) {
    if (!this.isEnabled || !this.isInitialized) return;

    try {
      const eventData = {
        page_title: pageTitle || `BeOnApex - ${pageName}`,
        page_location: window.location.href,
        custom_map: {
          'custom_parameter_1': 'nascar_section'
        }
      };

      window.gtag('config', GA_TRACKING_ID, eventData);

      if (this.debugMode) {
        console.log('📈 Page view tracked:', pageName, eventData);
      }
    } catch (error) {
      console.error('❌ Failed to track page view:', error);
    }
  }

  // Track custom events
  trackEvent(eventName, eventData = {}) {
    if (!this.isEnabled || !this.isInitialized) return;

    try {
      const enrichedData = {
        event_category: 'nascar_interaction',
        event_label: eventData.label || eventName,
        value: eventData.value || 1,
        custom_parameter_1: eventData.section || 'unknown',
        ...eventData
      };

      window.gtag('event', eventName, enrichedData);

      if (this.debugMode) {
        console.log('🎯 Event tracked:', eventName, enrichedData);
      }
    } catch (error) {
      console.error('❌ Failed to track event:', error);
    }
  }

  // NASCAR-specific tracking methods
  trackDriverSelection(driverName, section) {
    this.trackEvent('driver_selected', {
      section: section,
      driver_name: driverName,
      label: `Driver: ${driverName} in ${section}`
    });
  }

  trackFantasyAction(action, details = {}) {
    this.trackEvent('fantasy_action', {
      section: 'fantasy',
      action_type: action,
      label: `Fantasy: ${action}`,
      ...details
    });
  }

  trackStatisticsView(statsType, additionalData = {}) {
    this.trackEvent('statistics_viewed', {
      section: 'statistics',
      stats_type: statsType,
      label: `Stats: ${statsType}`,
      ...additionalData
    });
  }

  trackRaceDataInteraction(raceInfo, interactionType) {
    this.trackEvent('race_data_interaction', {
      section: 'race_data',
      race_year: raceInfo.year || 'unknown',
      race_number: raceInfo.raceNumber || 'unknown',
      track_name: raceInfo.trackName || 'unknown',
      interaction_type: interactionType,
      label: `Race: ${raceInfo.trackName} (${raceInfo.year})`
    });
  }

  trackTimeSpent(section, timeInSeconds) {
    this.trackEvent('time_spent', {
      section: section,
      time_spent: timeInSeconds,
      label: `Time in ${section}: ${timeInSeconds}s`,
      value: Math.round(timeInSeconds)
    });
  }

  trackUserEngagement(engagementType, data = {}) {
    this.trackEvent('user_engagement', {
      engagement_type: engagementType,
      label: `Engagement: ${engagementType}`,
      ...data
    });
  }

  // Theme and UI interactions
  trackThemeChange(newTheme) {
    this.trackEvent('theme_changed', {
      section: 'ui',
      theme: newTheme,
      label: `Theme changed to ${newTheme}`
    });
  }

  trackTabSwitch(fromTab, toTab) {
    this.trackEvent('tab_switch', {
      section: 'navigation',
      from_tab: fromTab,
      to_tab: toTab,
      label: `Navigation: ${fromTab} → ${toTab}`
    });
  }

  // Error tracking
  trackError(error, context = 'unknown') {
    this.trackEvent('error_occurred', {
      section: 'error',
      error_message: error.message || 'Unknown error',
      error_context: context,
      label: `Error in ${context}: ${error.message || 'Unknown'}`
    });
  }

  // Performance tracking
  trackPerformance(metricName, value, context = {}) {
    this.trackEvent('performance_metric', {
      section: 'performance',
      metric_name: metricName,
      metric_value: value,
      label: `Performance: ${metricName}`,
      value: Math.round(value),
      ...context
    });
  }

  // Utility methods
  setUserId(userId) {
    if (!this.isEnabled || !this.isInitialized) return;
    
    try {
      window.gtag('config', GA_TRACKING_ID, {
        user_id: userId
      });
    } catch (error) {
      console.error('❌ Failed to set user ID:', error);
    }
  }

  setUserProperties(properties) {
    if (!this.isEnabled || !this.isInitialized) return;
    
    try {
      window.gtag('set', {
        custom_map: properties
      });
    } catch (error) {
      console.error('❌ Failed to set user properties:', error);
    }
  }

  // Debug methods
  enableDebugMode() {
    this.debugMode = true;
    console.log('🔍 Analytics debug mode enabled');
  }

  disableDebugMode() {
    this.debugMode = false;
  }

  getStatus() {
    return {
      isInitialized: this.isInitialized,
      isEnabled: this.isEnabled,
      debugMode: this.debugMode,
      trackingId: GA_TRACKING_ID
    };
  }
}

// Create singleton instance
const analytics = new AnalyticsManager();

// Export both the instance and the class for flexibility
export default analytics;
export { AnalyticsManager };

// Convenience method for easy imports
export const trackPageView = (pageName, pageTitle) => analytics.trackPageView(pageName, pageTitle);
export const trackEvent = (eventName, eventData) => analytics.trackEvent(eventName, eventData);
export const trackDriverSelection = (driverName, section) => analytics.trackDriverSelection(driverName, section);
export const trackFantasyAction = (action, details) => analytics.trackFantasyAction(action, details);
export const trackStatisticsView = (statsType, additionalData) => analytics.trackStatisticsView(statsType, additionalData);
export const trackRaceDataInteraction = (raceInfo, interactionType) => analytics.trackRaceDataInteraction(raceInfo, interactionType);
export const trackTimeSpent = (section, timeInSeconds) => analytics.trackTimeSpent(section, timeInSeconds);
export const trackThemeChange = (newTheme) => analytics.trackThemeChange(newTheme);
export const trackTabSwitch = (fromTab, toTab) => analytics.trackTabSwitch(fromTab, toTab);
export const trackError = (error, context) => analytics.trackError(error, context);
export const trackPerformance = (metricName, value, context) => analytics.trackPerformance(metricName, value, context);